import { useEffect, useId, useRef } from 'react';
import SafeImage from '../../components/SafeImage';
import { travelMediaApi } from '../../api/travelMediaApi';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { isTravelImageObjectKey } from './socialComposeModel';
import { SOCIAL_COMPOSE_PRODUCT_CHROME as composeChrome } from '../social-post/socialActionPresentation';
import {
  MAX_IMAGES_PER_POST,
  hasPendingMediaUpload,
  isSupportedUploadFile,
  moveMediaItem,
  nextMediaLocalId,
  normalizeMediaDisplayOrder,
  removeMediaItem,
  replaceMediaItemAt,
  selectionExceedsMax,
} from '../social-post/socialPostMediaModel';
import '../social-post/social-media.css';

const ACCEPT = 'image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif';

export default function SocialPostMediaEditor({
  items,
  onChange,
  disabled = false,
  onError,
  hideAddTrigger = false,
  inputId: inputIdProp,
}) {
  const generatedId = useId();
  const inputId = inputIdProp || generatedId;
  const replaceId = useRef('');
  const objectUrls = useRef(new Set());

  useEffect(() => () => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.current.clear();
  }, []);

  function trackUrl(url) {
    if (url && url.startsWith('blob:')) objectUrls.current.add(url);
    return url;
  }

  function releaseUrl(url) {
    if (url && objectUrls.current.has(url)) {
      URL.revokeObjectURL(url);
      objectUrls.current.delete(url);
    }
  }

  async function uploadFile(localId, file) {
    try {
      const objectKey = String(await travelMediaApi.uploadImage(file) || '').trim();
      if (!isTravelImageObjectKey(objectKey)) {
        throw new Error('Upload did not return a travel/image object key.');
      }
      onChange((current) => replaceMediaItemAt(current, localId, {
        fileReference: objectKey,
        previewUrl: resolveMemberMediaUrl(objectKey) || current.find((item) => item.localId === localId)?.previewUrl,
        uploadState: 'ready',
        error: '',
      }));
    } catch (cause) {
      onChange((current) => replaceMediaItemAt(current, localId, {
        uploadState: 'failed',
        error: '這張圖片上傳失敗，可重試或移除。',
      }));
      onError?.(cause);
    }
  }

  async function addFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length || disabled) return;
    const supported = files.filter(isSupportedUploadFile);
    if (supported.length !== files.length) {
      onError?.(new Error('只接受 JPEG、PNG 或 GIF。'));
    }
    if (!supported.length) return;
    if (selectionExceedsMax(items.length, supported.length)) {
      onError?.(new Error(`最多 ${MAX_IMAGES_PER_POST} 張圖片。請減少選擇後再試。`));
      return;
    }
    const created = supported.map((file) => {
      const previewUrl = trackUrl(URL.createObjectURL(file));
      return {
        localId: nextMediaLocalId(),
        fileReference: '',
        previewUrl,
        uploadState: 'uploading',
        displayOrder: 0,
        file,
        error: '',
      };
    });
    onChange((current) => normalizeMediaDisplayOrder([...current, ...created]));
    for (const item of created) {
      await uploadFile(item.localId, item.file);
    }
  }

  function onPick(event) {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = '';
    addFiles(selectedFiles);
  }

  function onReplacePick(event) {
    const file = event.target.files?.[0];
    const localId = replaceId.current;
    event.target.value = '';
    replaceId.current = '';
    if (!file || !localId || disabled) return;
    if (!isSupportedUploadFile(file)) {
      onError?.(new Error('只接受 JPEG、PNG 或 GIF。'));
      return;
    }
    const previewUrl = trackUrl(URL.createObjectURL(file));
    const previous = items.find((item) => item.localId === localId);
    if (previous?.previewUrl) releaseUrl(previous.previewUrl);
    onChange((current) => replaceMediaItemAt(current, localId, {
      fileReference: '',
      previewUrl,
      uploadState: 'uploading',
      error: '',
    }));
    uploadFile(localId, file);
  }

  const canAdd = !disabled && items.length < MAX_IMAGES_PER_POST;
  const cover = items[0];
  const rest = items.slice(1);

  return (
    <div className="social-media-editor" data-testid="social-media-editor">
      <input
        id={inputId}
        type="file"
        accept={ACCEPT}
        multiple
        disabled={!canAdd}
        onChange={onPick}
        data-testid="social-media-add-input"
      />
      <input
        data-testid="social-media-replace-input"
        type="file"
        accept={ACCEPT}
        hidden
        disabled={disabled}
        onChange={onReplacePick}
      />

      {items.length ? (
        <div className="social-media-editor__gallery" data-testid="social-media-gallery">
          <div className="social-media-editor__cover">
            <SafeImage
              className="social-media-editor__thumb social-media-editor__thumb--cover"
              src={cover.previewUrl}
              alt={`圖片預覽 1`}
            />
            <span className="social-media-editor__badge">
              1 {composeChrome.coverBadge}
            </span>
            <button
              type="button"
              className="social-media-editor__remove"
              aria-label="移除圖片 1"
              disabled={disabled}
              onClick={() => {
                releaseUrl(cover.previewUrl);
                onChange(removeMediaItem(items, cover.localId));
              }}
            >
              ×
            </button>
            <p className="social-media-editor__reorder-hint">{composeChrome.reorderHint}</p>
            <div className="social-media-editor__cover-actions">
              <button
                type="button"
                aria-label="向右移動圖片 1"
                disabled={disabled || items.length < 2}
                onClick={() => onChange(moveMediaItem(items, cover.localId, 1))}
              >
                右移
              </button>
              <button
                type="button"
                aria-label="替換圖片 1"
                disabled={disabled}
                onClick={() => {
                  replaceId.current = cover.localId;
                  document.querySelector('[data-testid="social-media-replace-input"]')?.click();
                }}
              >
                替換
              </button>
              {cover.uploadState === 'failed' ? (
                <button
                  type="button"
                  aria-label="重試圖片 1"
                  disabled={disabled || !cover.file}
                  onClick={() => cover.file && uploadFile(cover.localId, cover.file)}
                >
                  重試
                </button>
              ) : null}
            </div>
            {cover.uploadState === 'uploading' ? <span className="social-media-editor__status">上傳中…</span> : null}
            {cover.uploadState === 'failed' ? <span className="social-media-editor__status" role="alert">上傳失敗</span> : null}
            {cover.error ? <span role="alert">{cover.error}</span> : null}
          </div>

          <div className="social-media-editor__side">
            {rest.map((item, offset) => {
              const index = offset + 1;
              return (
                <div key={item.localId} className="social-media-editor__side-item">
                  <SafeImage
                    className="social-media-editor__thumb social-media-editor__thumb--side"
                    src={item.previewUrl}
                    alt={`圖片預覽 ${index + 1}`}
                  />
                  <span className="social-media-editor__badge">{index + 1}</span>
                  <button
                    type="button"
                    className="social-media-editor__remove"
                    aria-label={`移除圖片 ${index + 1}`}
                    disabled={disabled}
                    onClick={() => {
                      releaseUrl(item.previewUrl);
                      onChange(removeMediaItem(items, item.localId));
                    }}
                  >
                    ×
                  </button>
                  <div className="social-media-editor__side-actions">
                    <button
                      type="button"
                      aria-label={`向左移動圖片 ${index + 1}`}
                      disabled={disabled}
                      onClick={() => onChange(moveMediaItem(items, item.localId, -1))}
                    >
                      左移
                    </button>
                    <button
                      type="button"
                      aria-label={`向右移動圖片 ${index + 1}`}
                      disabled={disabled || index === items.length - 1}
                      onClick={() => onChange(moveMediaItem(items, item.localId, 1))}
                    >
                      右移
                    </button>
                    <button
                      type="button"
                      aria-label={`替換圖片 ${index + 1}`}
                      disabled={disabled}
                      onClick={() => {
                        replaceId.current = item.localId;
                        document.querySelector('[data-testid="social-media-replace-input"]')?.click();
                      }}
                    >
                      替換
                    </button>
                    {item.uploadState === 'failed' ? (
                      <button
                        type="button"
                        aria-label={`重試圖片 ${index + 1}`}
                        disabled={disabled || !item.file}
                        onClick={() => item.file && uploadFile(item.localId, item.file)}
                      >
                        重試
                      </button>
                    ) : null}
                  </div>
                  {item.uploadState === 'uploading' ? <span className="social-media-editor__status">上傳中…</span> : null}
                  {item.error ? <span role="alert">{item.error}</span> : null}
                </div>
              );
            })}
            {canAdd && !hideAddTrigger ? (
              <label className="social-media-editor__add-tile" htmlFor={inputId}>
                {composeChrome.addImage}
              </label>
            ) : null}
          </div>
        </div>
      ) : null}

      {!items.length && !hideAddTrigger ? (
        <label className="social-media-editor__add" htmlFor={inputId}>{composeChrome.addImage}</label>
      ) : null}

      <p className="social-media-editor__count" data-testid="social-media-count">
        {composeChrome.mediaCount(items.length, MAX_IMAGES_PER_POST)}
      </p>
      {hasPendingMediaUpload(items) ? <p role="status">還有圖片未完成上傳，完成或移除後才能送出。</p> : null}
    </div>
  );
}
